/**
 * Server-backed account preferences.
 *
 * Semantic preferences (nav order, view modes, column sets, filter state) live
 * on the server so they follow the account to another browser or device;
 * device ergonomics (dark mode, layout density, side padding) and the
 * Analytics Reference Date stay in localStorage by design — see
 * docs/technical/FRONTEND_ARCHITECTURE.md#preference-architecture.
 *
 * Local-first with the server as truth:
 *   1. hydrate synchronously from a local cache so there is no flash of
 *      default layout,
 *   2. GET the row and adopt it (except keys with a write still pending),
 *   3. mutate locally at once, then PUT debounced — a preference write that
 *      fails keeps the local value, shows a quiet indicator and retries on the
 *      next mutation. It must never block the UI.
 *
 * ``usePreferences()`` returns null when no provider is mounted, which is the
 * signal for every preference hook to fall back to its localStorage-only
 * behaviour (this keeps component tests provider-free).
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  ReactNode,
} from 'react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export const USER_SETTINGS_CACHE_KEY = 'kalpa-user-settings';
export const SETTINGS_MIGRATED_KEY = 'kalpa-settings-migrated';
export const PREFERENCE_PUT_DEBOUNCE_MS = 500;

export type ViewModeValue = 'grid' | 'list';
export type HabitTitleClickAction = 'stats' | 'history' | 'edit' | 'log' | 'none';
export type TagFilterMode = 'any' | 'all';

/** JSON namespaces, mirroring schemas.USER_SETTINGS_NAMESPACES. */
export const PREFERENCE_NAMESPACES = [
  'nav_preferences',
  'habits_columns',
  'habits_filters',
  'life_events_display',
  'panel_states',
  'habit_stats',
  'view_modes',
  'backfill_timeline',
  'check_in',
  'task_planning',
] as const;

export type PreferenceNamespace = (typeof PREFERENCE_NAMESPACES)[number];

export type TypedPreference =
  | 'default_view_mode'
  | 'habit_title_click_action'
  | 'tag_filter_mode'
  | 'check_in_cutoff_hour';

export interface UserSettings {
  default_view_mode: ViewModeValue;
  habit_title_click_action: HabitTitleClickAction;
  tag_filter_mode: TagFilterMode;
  /** Local hour (0-23) at which the frozen check-in queue rolls to a new day. */
  check_in_cutoff_hour: number;
  nav_preferences: Record<string, unknown>;
  habits_columns: Record<string, unknown>;
  habits_filters: Record<string, unknown>;
  life_events_display: Record<string, unknown>;
  panel_states: Record<string, unknown>;
  habit_stats: Record<string, unknown>;
  view_modes: Record<string, unknown>;
  backfill_timeline: Record<string, unknown>;
  check_in: Record<string, unknown>;
  task_planning: Record<string, unknown>;
}

export const DEFAULT_USER_SETTINGS: UserSettings = {
  default_view_mode: 'grid',
  habit_title_click_action: 'stats',
  tag_filter_mode: 'any',
  check_in_cutoff_hour: 3,
  nav_preferences: {},
  habits_columns: {},
  habits_filters: {},
  life_events_display: {},
  panel_states: {},
  habit_stats: {},
  view_modes: {},
  backfill_timeline: {},
  check_in: {},
  task_planning: {},
};

/** Legacy per-feature keys: still written as a cache, and the migration source. */
export const LEGACY_VIEW_MODE_KEYS: Record<string, string> = {
  timers: 'kalpa-timers-view-mode',
  habits: 'kalpa-view-mode-habits',
  'check-in': 'kalpa-view-mode-check-in',
  projects: 'kalpa-view-mode-projects',
  reflections: 'kalpa-view-mode-reflections',
  timelines: 'kalpa-view-mode-timelines',
  'work-timers': 'kalpa-view-mode-work-timers',
};

const LEGACY_NAV_KEY = 'kalpa-nav-preferences';
const LEGACY_COLUMNS_KEY = 'kalpa-habits-columns';
const LEGACY_STATS_RANGE_KEY = 'kalpa-habit-stats-range-settings';
const LEGACY_STATS_LENS_KEY = 'kalpa-habit-stats-health-lens-settings';

export type SyncStatus = 'cached' | 'synced' | 'error';

export interface PreferencesContextValue {
  settings: UserSettings;
  syncStatus: SyncStatus;
  setTyped: (field: TypedPreference, value: string | number) => void;
  setNamespace: (namespace: PreferenceNamespace, value: Record<string, unknown>) => void;
  resetNamespace: (namespace: PreferenceNamespace) => void;
}

const PreferencesContext = createContext<PreferencesContextValue | null>(null);

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** Defensive load of the consolidated cache — never throws, always complete. */
export const loadCachedSettings = (): UserSettings => {
  try {
    const raw = localStorage.getItem(USER_SETTINGS_CACHE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (isPlainObject(parsed)) {
        return mergeSettings(parsed);
      }
    }
  } catch {
    // Corrupt or inaccessible cache falls through to the legacy seed.
  }
  return seedFromLegacyKeys();
};

const mergeSettings = (partial: Record<string, unknown>): UserSettings => {
  const merged: UserSettings = { ...DEFAULT_USER_SETTINGS };
  if (partial.default_view_mode === 'grid' || partial.default_view_mode === 'list') {
    merged.default_view_mode = partial.default_view_mode;
  }
  if (typeof partial.habit_title_click_action === 'string') {
    merged.habit_title_click_action = partial.habit_title_click_action as HabitTitleClickAction;
  }
  if (partial.tag_filter_mode === 'any' || partial.tag_filter_mode === 'all') {
    merged.tag_filter_mode = partial.tag_filter_mode;
  }
  if (
    typeof partial.check_in_cutoff_hour === 'number' &&
    Number.isInteger(partial.check_in_cutoff_hour) &&
    partial.check_in_cutoff_hour >= 0 &&
    partial.check_in_cutoff_hour <= 23
  ) {
    merged.check_in_cutoff_hour = partial.check_in_cutoff_hour;
  }
  PREFERENCE_NAMESPACES.forEach((namespace) => {
    const value = partial[namespace];
    if (isPlainObject(value)) {
      merged[namespace] = value;
    }
  });
  return merged;
};

// ---------------------------------------------------------------------------
// life_events_display — typed shape of the Life Events (timers) namespace.
// Mirrors schemas.LifeEventsDisplaySettings. The loader is defensive in the
// same way loadRangeSettings is: unknown or ill-typed fields fall back to
// their defaults, and the defaults reproduce the pre-configurable duration
// rendering exactly, so an empty namespace changes nothing on screen.
// ---------------------------------------------------------------------------

export const DURATION_LARGEST_UNITS = ['years', 'months', 'weeks', 'days'] as const;
export type DurationLargestUnit = (typeof DURATION_LARGEST_UNITS)[number];

export interface TimerViewFilters {
  tags: string[];
  tag_mode: TagFilterMode;
  recurrence_types: string[];
  recurrence_interval_min: number | null;
  recurrence_interval_max: number | null;
  timer_type: 'countdown' | 'count_up' | 'any';
  state: string[];
  search: string;
}

export interface TimerViewSort {
  field: string;
  direction: 'asc' | 'desc';
}

export interface SavedTimerView {
  id: string;
  name: string;
  icon: string | null;
  filters: TimerViewFilters;
  sort: TimerViewSort;
  /** null = follow the account-wide default view mode. */
  view_mode: ViewModeValue | null;
  columns: Record<string, boolean>;
}

export interface LifeEventsDisplaySettings {
  saved_views: SavedTimerView[];
  default_view_id: string | null;
  filter_bar_collapsed: boolean;
  duration_largest_unit: DurationLargestUnit;
  /**
   * Number of calendar units rendered, counting down from the largest
   * (years → months → weeks → days). The h:m:s clock is a separate toggle.
   */
  duration_precision: number;
  duration_show_clock: boolean;
  duration_show_workdays: boolean;
}

export const DEFAULT_LIFE_EVENTS_DISPLAY: LifeEventsDisplaySettings = {
  saved_views: [],
  default_view_id: null,
  filter_bar_collapsed: false,
  duration_largest_unit: 'days',
  duration_precision: 1,
  duration_show_clock: true,
  duration_show_workdays: false,
};

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((entry) => typeof entry === 'string');

const parseBooleanMap = (value: unknown): Record<string, boolean> => {
  if (!isPlainObject(value)) return {};
  const map: Record<string, boolean> = {};
  Object.entries(value).forEach(([key, entry]) => {
    if (typeof entry === 'boolean') map[key] = entry;
  });
  return map;
};

const parseTimerViewFilters = (value: unknown): TimerViewFilters => {
  const raw: Record<string, unknown> = isPlainObject(value) ? value : {};
  return {
    tags: isStringArray(raw.tags) ? raw.tags : [],
    tag_mode: raw.tag_mode === 'all' ? 'all' : 'any',
    recurrence_types: isStringArray(raw.recurrence_types) ? raw.recurrence_types : [],
    recurrence_interval_min:
      typeof raw.recurrence_interval_min === 'number' ? raw.recurrence_interval_min : null,
    recurrence_interval_max:
      typeof raw.recurrence_interval_max === 'number' ? raw.recurrence_interval_max : null,
    timer_type:
      raw.timer_type === 'countdown' || raw.timer_type === 'count_up' ? raw.timer_type : 'any',
    state: isStringArray(raw.state) ? raw.state : [],
    search: typeof raw.search === 'string' ? raw.search : '',
  };
};

const parseSavedTimerView = (value: unknown): SavedTimerView | null => {
  if (!isPlainObject(value)) return null;
  if (typeof value.id !== 'string' || typeof value.name !== 'string') return null;
  const sort: Record<string, unknown> = isPlainObject(value.sort) ? value.sort : {};
  return {
    id: value.id,
    name: value.name,
    icon: typeof value.icon === 'string' ? value.icon : null,
    filters: parseTimerViewFilters(value.filters),
    sort: {
      field: typeof sort.field === 'string' ? sort.field : 'target_date',
      direction: sort.direction === 'desc' ? 'desc' : 'asc',
    },
    view_mode:
      value.view_mode === 'grid' || value.view_mode === 'list' ? value.view_mode : null,
    columns: parseBooleanMap(value.columns),
  };
};

/** Defensive load of the namespace — never throws, always complete. */
export const loadLifeEventsDisplaySettings = (
  stored?: Record<string, unknown> | null,
): LifeEventsDisplaySettings => {
  const raw: Record<string, unknown> = stored && isPlainObject(stored) ? stored : {};
  const parsed: LifeEventsDisplaySettings = { ...DEFAULT_LIFE_EVENTS_DISPLAY };
  if (Array.isArray(raw.saved_views)) {
    parsed.saved_views = raw.saved_views
      .map(parseSavedTimerView)
      .filter((view): view is SavedTimerView => view !== null);
  }
  if (typeof raw.default_view_id === 'string') {
    parsed.default_view_id = raw.default_view_id;
  }
  if (typeof raw.filter_bar_collapsed === 'boolean') {
    parsed.filter_bar_collapsed = raw.filter_bar_collapsed;
  }
  if (DURATION_LARGEST_UNITS.includes(raw.duration_largest_unit as DurationLargestUnit)) {
    parsed.duration_largest_unit = raw.duration_largest_unit as DurationLargestUnit;
  }
  if (
    typeof raw.duration_precision === 'number' &&
    Number.isFinite(raw.duration_precision)
  ) {
    parsed.duration_precision = Math.min(4, Math.max(1, Math.floor(raw.duration_precision)));
  }
  if (typeof raw.duration_show_clock === 'boolean') {
    parsed.duration_show_clock = raw.duration_show_clock;
  }
  if (typeof raw.duration_show_workdays === 'boolean') {
    parsed.duration_show_workdays = raw.duration_show_workdays;
  }
  return parsed;
};

// ---------------------------------------------------------------------------
// backfill_timeline — typed shape of the backfill wizard's timeline namespace.
// Mirrors schemas.BackfillTimelineSettings. The defaults reproduce the ratified
// behaviour, so an untouched account needs no write.
// ---------------------------------------------------------------------------

/**
 * Neighbour-propagation rule for an edge drag, in video-editing terms: `roll`
 * pins the neighbour's outer edge and absorbs the change by shrinking it;
 * `ripple` preserves its duration and slides it.
 */
export type TimelineEditMode = 'roll' | 'ripple';

export interface BackfillTimelineSettings {
  edit_mode: TimelineEditMode;
  snap_minutes: number;
  show_timeline: boolean;
}

export const DEFAULT_BACKFILL_TIMELINE: BackfillTimelineSettings = {
  edit_mode: 'ripple',
  snap_minutes: 5,
  show_timeline: true,
};

/** Defensive load of the namespace — never throws, always complete. */
export const loadBackfillTimelineSettings = (
  stored?: Record<string, unknown> | null,
): BackfillTimelineSettings => {
  const raw: Record<string, unknown> = stored && isPlainObject(stored) ? stored : {};
  const parsed: BackfillTimelineSettings = { ...DEFAULT_BACKFILL_TIMELINE };
  if (raw.edit_mode === 'roll' || raw.edit_mode === 'ripple') {
    parsed.edit_mode = raw.edit_mode;
  }
  if (typeof raw.snap_minutes === 'number' && Number.isFinite(raw.snap_minutes)) {
    parsed.snap_minutes = Math.min(60, Math.max(1, Math.floor(raw.snap_minutes)));
  }
  if (typeof raw.show_timeline === 'boolean') {
    parsed.show_timeline = raw.show_timeline;
  }
  return parsed;
};

// ---------------------------------------------------------------------------
// habits_filters — typed shape of the Habits sort/filter namespace.
// Mirrors schemas.HabitsFiltersSettings. The loader is defensive the same way
// loadLifeEventsDisplaySettings is: unknown or ill-typed fields fall back to
// their defaults, and the defaults reproduce the unfiltered, default-ordered
// list exactly, so an empty namespace changes nothing on screen.
// ---------------------------------------------------------------------------

export const HABIT_SORT_KEYS = [
  'title',
  'frequency',
  'progress_today',
  'next_due',
  'created',
  'updated',
  'check_in_priority',
] as const;
export type HabitSortKey = (typeof HABIT_SORT_KEYS)[number];
export type HabitProgressTodayFilter = 'untouched' | 'partial' | 'complete';
export type HabitCheckInFilter = 'enabled' | 'disabled';
export type HabitStatusFilter = 'active' | 'archived' | 'all';

/** Mirrors schemas.HabitsFiltersSettings.presets max_length. */
export const HABITS_FILTER_PRESET_LIMIT = 10;

export interface HabitsFilterState {
  /** null = the list's default ordering (no sort param sent). */
  sort: HabitSortKey | null;
  /** null = the key's own default (progress_today descends, others ascend). */
  order: 'asc' | 'desc' | null;
  status: HabitStatusFilter;
  tags: string[];
  tag_mode: TagFilterMode;
  progress_today: HabitProgressTodayFilter | null;
  check_in: HabitCheckInFilter | null;
  q: string;
}

export interface HabitsFilterPreset extends HabitsFilterState {
  id: string;
  name: string;
}

export interface HabitsFiltersSettings {
  /** The state to restore on the next visit; null = defaults. */
  active: HabitsFilterState | null;
  presets: HabitsFilterPreset[];
}

export const DEFAULT_HABITS_FILTER_STATE: HabitsFilterState = {
  sort: null,
  order: null,
  status: 'active',
  tags: [],
  tag_mode: 'any',
  progress_today: null,
  check_in: null,
  q: '',
};

export const DEFAULT_HABITS_FILTERS: HabitsFiltersSettings = {
  active: null,
  presets: [],
};

const parseHabitsFilterState = (value: unknown): HabitsFilterState => {
  const raw: Record<string, unknown> = isPlainObject(value) ? value : {};
  return {
    sort: HABIT_SORT_KEYS.includes(raw.sort as HabitSortKey)
      ? (raw.sort as HabitSortKey)
      : null,
    order: raw.order === 'asc' || raw.order === 'desc' ? raw.order : null,
    status:
      raw.status === 'archived' || raw.status === 'all' ? raw.status : 'active',
    tags: isStringArray(raw.tags) ? raw.tags : [],
    tag_mode: raw.tag_mode === 'all' ? 'all' : 'any',
    progress_today:
      raw.progress_today === 'untouched' ||
      raw.progress_today === 'partial' ||
      raw.progress_today === 'complete'
        ? raw.progress_today
        : null,
    check_in:
      raw.check_in === 'enabled' || raw.check_in === 'disabled'
        ? raw.check_in
        : null,
    q: typeof raw.q === 'string' ? raw.q : '',
  };
};

const parseHabitsFilterPreset = (value: unknown): HabitsFilterPreset | null => {
  if (!isPlainObject(value)) return null;
  if (typeof value.id !== 'string' || typeof value.name !== 'string') return null;
  return { ...parseHabitsFilterState(value), id: value.id, name: value.name };
};

/** Defensive load of the namespace — never throws, always complete. */
export const loadHabitsFiltersSettings = (
  stored?: Record<string, unknown> | null,
): HabitsFiltersSettings => {
  const raw: Record<string, unknown> = stored && isPlainObject(stored) ? stored : {};
  return {
    active: isPlainObject(raw.active) ? parseHabitsFilterState(raw.active) : null,
    presets: Array.isArray(raw.presets)
      ? raw.presets
          .map(parseHabitsFilterPreset)
          .filter((preset): preset is HabitsFilterPreset => preset !== null)
          .slice(0, HABITS_FILTER_PRESET_LIMIT)
      : [],
  };
};

const readJson = (key: string): Record<string, unknown> | null => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return isPlainObject(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

/**
 * Build a settings object from the pre-server localStorage keys. Used both as
 * the first-paint value before the server responds and as the payload of the
 * one-time upload.
 */
export const seedFromLegacyKeys = (): UserSettings => {
  const seeded: UserSettings = { ...DEFAULT_USER_SETTINGS };
  const nav = readJson(LEGACY_NAV_KEY);
  if (nav) seeded.nav_preferences = nav;
  const columns = readJson(LEGACY_COLUMNS_KEY);
  if (columns) seeded.habits_columns = columns;

  const ranges = readJson(LEGACY_STATS_RANGE_KEY);
  const lens = readJson(LEGACY_STATS_LENS_KEY);
  if (ranges || lens) {
    const habitStats: Record<string, unknown> = {};
    if (ranges) habitStats.enabled_ranges = ranges;
    if (lens && typeof lens.defaultLens === 'string') {
      habitStats.default_lens = lens.defaultLens;
    }
    seeded.habit_stats = habitStats;
  }

  const viewModes: Record<string, ViewModeValue> = {};
  Object.entries(LEGACY_VIEW_MODE_KEYS).forEach(([surface, key]) => {
    try {
      const stored = localStorage.getItem(key);
      if (stored === 'grid' || stored === 'list') {
        viewModes[surface] = stored;
      }
    } catch {
      // Ignore inaccessible storage.
    }
  });
  if (Object.keys(viewModes).length > 0) {
    seeded.view_modes = viewModes;
  }
  return seeded;
};

const hasAnyLocalPreference = (seed: UserSettings): boolean =>
  PREFERENCE_NAMESPACES.some((namespace) => Object.keys(seed[namespace]).length > 0);

const isServerRowPristine = (settings: UserSettings): boolean =>
  settings.default_view_mode === DEFAULT_USER_SETTINGS.default_view_mode &&
  settings.habit_title_click_action === DEFAULT_USER_SETTINGS.habit_title_click_action &&
  settings.tag_filter_mode === DEFAULT_USER_SETTINGS.tag_filter_mode &&
  settings.check_in_cutoff_hour === DEFAULT_USER_SETTINGS.check_in_cutoff_hour &&
  PREFERENCE_NAMESPACES.every((namespace) => Object.keys(settings[namespace]).length === 0);

const writeCache = (settings: UserSettings): void => {
  try {
    localStorage.setItem(USER_SETTINGS_CACHE_KEY, JSON.stringify(settings));
  } catch {
    // Cache is an optimization; the in-memory value still works this session.
  }
};

interface PreferencesProviderProps {
  children: ReactNode;
  /** Skip the server round-trip (used by tests and unauthenticated screens). */
  enabled?: boolean;
}

export const PreferencesProvider: React.FC<PreferencesProviderProps> = ({
  children,
  enabled = true,
}) => {
  const [settings, setSettings] = useState<UserSettings>(loadCachedSettings);
  const [syncStatus, setSyncStatus] = useState<SyncStatus>('cached');

  const pendingRef = useRef<Record<string, unknown>>({});
  const timerRef = useRef<number | null>(null);
  const settingsRef = useRef(settings);
  settingsRef.current = settings;

  const flush = useCallback(async () => {
    // Snapshot: the pending object is mutated below as keys are acknowledged,
    // and the request payload must not change underneath the in-flight call.
    const patch = { ...pendingRef.current };
    const keys = Object.keys(patch);
    if (keys.length === 0) {
      return;
    }
    try {
      const response = await axios.put(`${API_BASE_URL}/api/users/settings`, patch);
      // Clear only what we just sent — mutations made mid-flight stay queued.
      keys.forEach((key) => {
        if (pendingRef.current[key] === patch[key]) {
          delete pendingRef.current[key];
        }
      });
      if (response.data) {
        const merged = mergeSettings(response.data);
        // Keys still pending are newer than this response (they were changed
        // while the request was in flight); adopting the response over them
        // would silently revert the user's latest edit.
        const stillPending = Object.keys(pendingRef.current);
        stillPending.forEach((key) => {
          (merged as unknown as Record<string, unknown>)[key] = (
            settingsRef.current as unknown as Record<string, unknown>
          )[key];
        });
        setSettings(merged);
        writeCache(merged);
      }
      setSyncStatus(Object.keys(pendingRef.current).length > 0 ? 'cached' : 'synced');
    } catch {
      // Keep the local value AND the pending patch: the next mutation retries.
      setSyncStatus('error');
    }
  }, []);

  const scheduleFlush = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
    }
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      flush();
    }, PREFERENCE_PUT_DEBOUNCE_MS);
  }, [flush]);

  const applyLocal = useCallback(
    (patch: Partial<UserSettings>) => {
      setSettings((prev) => {
        const next = { ...prev, ...patch };
        writeCache(next);
        return next;
      });
    },
    [],
  );

  const queue = useCallback(
    (field: string, value: unknown) => {
      pendingRef.current = { ...pendingRef.current, [field]: value };
      scheduleFlush();
    },
    [scheduleFlush],
  );

  const setTyped = useCallback(
    (field: TypedPreference, value: string | number) => {
      applyLocal({ [field]: value } as Partial<UserSettings>);
      queue(field, value);
    },
    [applyLocal, queue],
  );

  const setNamespace = useCallback(
    (namespace: PreferenceNamespace, value: Record<string, unknown>) => {
      applyLocal({ [namespace]: value } as Partial<UserSettings>);
      queue(namespace, value);
    },
    [applyLocal, queue],
  );

  const resetNamespace = useCallback(
    (namespace: PreferenceNamespace) => {
      applyLocal({ [namespace]: {} } as Partial<UserSettings>);
      // Explicit null is the wire representation of "reset to default".
      queue(namespace, null);
    },
    [applyLocal, queue],
  );

  // Hydrate from the server, then run the one-time local→server migration.
  useEffect(() => {
    if (!enabled) {
      return;
    }
    let cancelled = false;
    const hydrate = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/users/settings`);
        if (cancelled) return;
        const server = mergeSettings(response.data || {});

        let migrated = true;
        try {
          migrated = localStorage.getItem(SETTINGS_MIGRATED_KEY) === '1';
        } catch {
          migrated = true;
        }

        if (!migrated && isServerRowPristine(server)) {
          const seed = seedFromLegacyKeys();
          if (hasAnyLocalPreference(seed)) {
            // First run after the upgrade: lift this browser's preferences to
            // the account once. Legacy keys are left in place as the cache.
            const payload: Record<string, unknown> = {};
            PREFERENCE_NAMESPACES.forEach((namespace) => {
              if (Object.keys(seed[namespace]).length > 0) {
                payload[namespace] = seed[namespace];
              }
            });
            try {
              const uploaded = await axios.put(
                `${API_BASE_URL}/api/users/settings`,
                payload,
              );
              if (cancelled) return;
              const merged = mergeSettings(uploaded.data || seed);
              setSettings(merged);
              writeCache(merged);
              localStorage.setItem(SETTINGS_MIGRATED_KEY, '1');
              setSyncStatus('synced');
              return;
            } catch {
              // Leave the marker unset so the next load retries.
              setSyncStatus('error');
              return;
            }
          }
        }
        if (!migrated) {
          // Nothing to lift, or another browser migrated first.
          try {
            localStorage.setItem(SETTINGS_MIGRATED_KEY, '1');
          } catch {
            // Non-fatal.
          }
        }

        // Server wins, except fields with a write still in flight.
        const pendingKeys = Object.keys(pendingRef.current);
        const adopted = { ...server };
        pendingKeys.forEach((key) => {
          (adopted as Record<string, unknown>)[key] =
            (settingsRef.current as unknown as Record<string, unknown>)[key];
        });
        setSettings(adopted);
        writeCache(adopted);
        setSyncStatus('synced');
      } catch {
        if (!cancelled) {
          // Offline or unauthenticated: keep painting from the cache.
          setSyncStatus('error');
        }
      }
    };
    hydrate();
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  // Best-effort flush of a pending write when the tab goes away.
  useEffect(() => {
    const onHide = () => {
      if (Object.keys(pendingRef.current).length > 0) {
        flush();
      }
    };
    window.addEventListener('beforeunload', onHide);
    return () => {
      window.removeEventListener('beforeunload', onHide);
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current);
      }
    };
  }, [flush]);

  const value: PreferencesContextValue = {
    settings,
    syncStatus,
    setTyped,
    setNamespace,
    resetNamespace,
  };

  return (
    <PreferencesContext.Provider value={value}>{children}</PreferencesContext.Provider>
  );
};

/** Null when no provider is mounted — callers fall back to localStorage. */
export const usePreferences = (): PreferencesContextValue | null =>
  useContext(PreferencesContext);

/**
 * Typed view of the life_events_display namespace. Provider-free mounts (and
 * component tests) get the defaults, which reproduce the pre-configurable
 * duration rendering exactly. Writers go through
 * setNamespace('life_events_display', …) with a complete value.
 */
export const useLifeEventsDisplaySettings = (): LifeEventsDisplaySettings => {
  const store = usePreferences();
  const namespace = store ? store.settings.life_events_display : null;
  return useMemo(() => loadLifeEventsDisplaySettings(namespace), [namespace]);
};

/**
 * Typed view of the habits_filters namespace. Provider-free mounts (and
 * component tests) get the defaults — an unfiltered, default-ordered list.
 * Writers go through setNamespace('habits_filters', …) with a complete value.
 */
export const useHabitsFiltersSettings = (): HabitsFiltersSettings => {
  const store = usePreferences();
  const namespace = store ? store.settings.habits_filters : null;
  return useMemo(() => loadHabitsFiltersSettings(namespace), [namespace]);
};

/**
 * Typed view of the backfill_timeline namespace. Provider-free mounts (and
 * component tests) get the defaults — ripple on a five-minute grid with the
 * timeline shown. Writers go through setNamespace('backfill_timeline', …).
 */
export const useBackfillTimelineSettings = (): BackfillTimelineSettings => {
  const store = usePreferences();
  const namespace = store ? store.settings.backfill_timeline : null;
  return useMemo(() => loadBackfillTimelineSettings(namespace), [namespace]);
};

export default PreferencesContext;
